"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useRouter } from "next/navigation";
import { pageVariants, cardVariants, slideFromBottomVariants, headerVariants } from "@/lib/animations";
import ProductDetailMenu, { type MenuItems } from "@/components/ui/ProductDetailMenu";
import ScrollProgressIndicator from "@/components/ui/ScrollProgressIndicator";
import ProductReviewsList from "@/components/ui/ProductReviewsList";
import ProductReviewForm from "@/components/ui/ProductReviewForm";
import { useProductYachtData } from "@/hooks/useProductYachtData";
import { useProductReviews, Review } from "@/hooks/useProductReviews";
import { useProductBreadcrumbs } from "@/hooks/useProductBreadcrumbs";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import ProductYachtFeatures from "./sections/ProductYachtFeatures";
import ProductYachtConditions from "./sections/ProductYachtConditions";
import ProductYachtPrices from "./sections/ProductYachtPrices";
import ProductYachtEquipment from "./sections/ProductYachtEquipment";
import ProductTourDescription from "./sections/ProductTourDescription";
import ProductShareButton from "@/components/ui/ProductShareButton";
import BrochureDownloadModal from "@/components/ui/BrochureDownloadModal";
import ProductTourServicesList from "@/components/ui/ProductTourServicesList";
import ReservationBookingSidebar from "@/components/ui/ReservationBookingSidebar";
import Faqs from "@/components/sections/Faqs";
import type { FaqEntry } from "@/types/faq";
import type { PageUrlMap } from "@/types/blog";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";
import { isOtherDescriptionsGroup, resolveRepeaterSectionTitle } from "@/utils/productOtherDescriptionsUtils";
import { resolveUrlFromMap } from "@/lib/blog-transformers";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import CustomerApiService from "@/services/CustomerApiService";
import ProductTourHero from "./sections/ProductTourHero";

interface ProductYachtDetailProps {
    product?: {
        id?: number;
        name?: string;
        type?: string;
        product_type?: string;
        description?: string;
        gallery?: Array<any>;
        metas?: Record<string, any>;
        fields?: Record<string, any>;
        monthly_prices?: any[];
        currency?: any;
        /** Yacht için minimum kiralama süresi (gün); API'den gelir */
        min_rental_duration?: number | null;
        /** Yat dönem başlangıç tarihi (YYYY-MM-DD); takvim sınırlaması */
        period_start_date?: string | null;
        /** Yat dönem bitiş tarihi (YYYY-MM-DD); takvim sınırlaması */
        period_end_date?: string | null;
        has_price?: boolean;
        booking_flow?: string;
        categories?: Array<{
            id?: number;
            name?: string;
            slug?: string;
        }>;
    };
    language?: string;
    languageId?: number;
    translations?: Record<string, string>;
    menuItems?: MenuItems[];
    reviewForm?: {
        id?: number;
        slug?: string;
        title?: string;
    } | null;
    reviews?: Review[];
    averageRating?: number;
    reviewCount?: number;
    hasMoreReviews?: boolean;
    pageUrls?: PageUrlMap;
    contractsPageUrl?: string;
    defaultLanguage?: string;
    initialFormDetails?: any;
    /** Yacht talep formu detayı (sayfa render'da çekilir); sidebar ek istek atmaz */
    initialRequestFormDetails?: any;
    /** Rezervasyon checkout sözleşmeleri (sunucudan gelir) */
    initialReservationFormAgreements?: Array<{ id: number; title: string; slug: string; is_required: boolean; content?: string }>;
    productFaqs?: FaqEntry[];
}

/** Ana sütun bölümleri — mobilde daha sıkı dikey ritim */
const sectionBlock =
    "mb-12 scroll-mt-20 sm:mb-16 sm:scroll-mt-24 lg:mb-24";

export default function ProductYachtDetail({
    product,
    language,
    languageId,
    translations = {},
    menuItems,
    reviewForm,
    reviews: initialReviews = [],
    averageRating: initialAverageRating = 0,
    reviewCount: initialReviewCount = 0,
    hasMoreReviews: initialHasMoreReviews = false,
    pageUrls,
    contractsPageUrl,
    defaultLanguage,
    initialFormDetails,
    initialRequestFormDetails,
    initialReservationFormAgreements = [],
    productFaqs = [],
}: ProductYachtDetailProps = {}) {
    const router = useRouter();
    const [isFavorite, setIsFavorite] = useState(false);
    const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);
    const [brochureModalOpen, setBrochureModalOpen] = useState(false);
    const [reservationFormAgreements, setReservationFormAgreements] = useState<Array<{ id: number; title: string; slug: string; is_required: boolean; content?: string }>>(initialReservationFormAgreements);
    const { isAuthenticated, customer } = useCustomerAuth();

    const { getVariable } = useLanguageVariables();
    const currentLanguage = language || getVariable('language', 'en');

    const yachtData = useProductYachtData(product, product?.monthly_prices, currentLanguage);
    const { breadcrumbs } = useProductBreadcrumbs(product, currentLanguage, true, pageUrls, defaultLanguage);

    const hasServerReviews = initialReviews.length > 0;
    const { reviews: clientReviews, averageRating: clientAverageRating, reviewCount: clientReviewCount, isLoading: reviewsLoading, refetch: refetchReviews, reviewsTriggerRef } = useProductReviews(
        product?.id || null,
        languageId || undefined,
        !hasServerReviews,
        { lazyLoad: true }
    );
    
    const reviews = hasServerReviews ? initialReviews : clientReviews;
    const averageRating = hasServerReviews && initialAverageRating > 0 ? initialAverageRating : clientAverageRating;
    const reviewCount = hasServerReviews && initialReviewCount > 0 ? initialReviewCount : clientReviewCount;
    const [hasMoreReviews, setHasMoreReviews] = useState(initialHasMoreReviews);
    const [currentPage, setCurrentPage] = useState(1);

    const effectiveReviewForm = reviewForm ?? { slug: 'product-review-form', title: 'Product Review Form' };

    useEffect(() => {
        if (initialReservationFormAgreements.length > 0) return;
        const productId = product?.id;
        const productType = product?.type || product?.product_type || 'yacht';
        if (!productId) {
            setReservationFormAgreements([]);
            return;
        }
        const params = new URLSearchParams({ product_id: String(productId), product_type: productType, for_reservation: '1' });
        if (languageId) params.set('language_id', String(languageId));
        fetch(`/api/forms/details?${params.toString()}`)
            .then((res) => res.json())
            .then((data) => {
                if (data?.success && data?.data?.agreements) {
                    const required = (data.data.agreements.required || []).map((a: { id: number; title: string; slug: string; content?: string }) => ({
                        id: a.id, title: a.title, slug: a.slug, is_required: true, content: a.content,
                    }));
                    const optional = (data.data.agreements.optional || []).map((a: { id: number; title: string; slug: string; content?: string }) => ({
                        id: a.id, title: a.title, slug: a.slug, is_required: false, content: a.content,
                    }));
                    setReservationFormAgreements([...required, ...optional]);
                } else {
                    setReservationFormAgreements([]);
                }
            })
            .catch(() => setReservationFormAgreements([]));
    }, [product?.id, product?.type, product?.product_type, languageId, initialReservationFormAgreements.length]);

    const t = useCallback((key: string, fallback?: string): string => {
        return translations[key] || getVariable(key, fallback || key);
    }, [translations, getVariable]);

    // Check favorite status from backend (ProductTourDetail gibi)
    useEffect(() => {
        if (!product?.id || !isAuthenticated || !customer) {
            setIsFavorite(false);
            return;
        }

        const checkFavorite = async () => {
            try {
                const productType = product?.type || product?.product_type || 'yacht';
                const result = await CustomerApiService.checkFavorite(product.id!, productType);
                setIsFavorite(result.isFavorite);
            } catch (error) {
                // Silently fail - user might not be logged in
                setIsFavorite(false);
            }
        };

        checkFavorite();
    }, [product?.id, product?.type, product?.product_type, isAuthenticated, customer]);

    const toggleFavorite = useCallback(async () => {
        if (!product?.id || !isAuthenticated || !customer) {
            // If not authenticated, show login prompt or redirect
            const loginUrl = resolveUrlFromMap(
                pageUrls,
                'login',
                currentLanguage,
                defaultLanguage ?? 'en',
                `/${currentLanguage}/login`
            );
            const returnPath = typeof window !== 'undefined' ? window.location.pathname : '';
            router.push(`${loginUrl}?return=${encodeURIComponent(returnPath)}`);
            return;
        }

        if (isFavoriteLoading) return;

        setIsFavoriteLoading(true);
        try {
            const productType = product?.type || product?.product_type || 'yacht';
            const result = await CustomerApiService.toggleFavorite(product.id, productType);
            setIsFavorite(result.isFavorite);
        } catch (error) {
            console.error('[ProductYachtDetail] Toggle favorite error:', error);
            // Show error message to user
            const errorMessage = error instanceof Error ? error.message : t('yacht_detail.favorite_error', 'Failed to update favorite');
            alert(errorMessage);
        } finally {
            setIsFavoriteLoading(false);
        }
    }, [isFavorite, isFavoriteLoading, product?.id, product?.type, product?.product_type, isAuthenticated, customer, currentLanguage, pageUrls, t, router, defaultLanguage]);

    /** Sayfadaki bölümlerin DOM sırası (menü ve scroll indicator ile aynı kaynak). */
    const contentOrderSections = useMemo((): Array<{ id: string; label: string; isOtherDescription?: boolean }> => {
        const sections: Array<{ id: string; label: string; isOtherDescription?: boolean }> = [];
        const sectionIds = new Set<string>();

        const push = (id: string, label: string, isOtherDescription?: boolean) => {
            if (!sectionIds.has(id)) {
                sectionIds.add(id);
                sections.push({ id, label, isOtherDescription });
            }
        };

        // DOM sırası: features → price-detail → information → equipments → general-conditions → services → tab fields → faqs → reviews
        if (yachtData.features.length > 0) push('features', t('features', 'Features'));
        if (yachtData.monthlyPrices.length > 0) push('price-detail', t('price', 'Price'));
        const hasInformationContent = (product?.description && !yachtData.information.content) || !!yachtData.information.content;
        if (hasInformationContent) push('information', t('information', 'Information'));
        if (yachtData.equipment.length > 0) push('equipments', t('equipments', 'Equipments'));
        if (yachtData.conditions.length > 0) push('general-conditions', t('general_conditions', 'General Conditions'));
        const hasServices = (yachtData.includedServices.length > 0 || yachtData.excludedServices.length > 0) ||
            !!(yachtData.servicesContent.includedContent || yachtData.servicesContent.excludedContent);
        if (hasServices) push('services', t('services', 'Services'));
        yachtData.tabFields.forEach((tabGroup) => {
            const label = resolveRepeaterSectionTitle(tabGroup.tabName, tabGroup.tabName, t);
            const isOther = isOtherDescriptionsGroup((tabGroup as { groupKey?: string }).groupKey);
            push(tabGroup.sectionId, label, isOther);
        });
        if (productFaqs && productFaqs.length > 0) push('faqs', t('faqs', 'FAQs'));
        push('reviews', t('reviews', 'Reviews'));

        return sections;
    }, [yachtData.features.length, yachtData.monthlyPrices.length, product?.description, yachtData.information, yachtData.equipment.length, yachtData.conditions.length, yachtData.includedServices.length, yachtData.excludedServices.length, yachtData.servicesContent, yachtData.tabFields, productFaqs, t]);

    /** Menü öğeleri: contentOrderSections ile aynı sıra; Diğer Açıklamalar gruplu dropdown. */
    const displayMenuItems = useMemo((): MenuItems[] => {
        const items: MenuItems[] = [];
        let otherDescriptionsBuffer: Array<{ id: string; label: string }> = [];

        const flushOtherDescriptions = () => {
            if (otherDescriptionsBuffer.length === 0) return;
            items.push({
                title: t('other_descriptions', 'Diğer Açıklamalar'),
                link: '#other-descriptions',
                svg: '',
                isDropdown: true,
                children: otherDescriptionsBuffer.map((c) => ({ title: c.label, link: `#${c.id}` })),
            });
            otherDescriptionsBuffer = [];
        };

        contentOrderSections.forEach((sec) => {
            if (sec.isOtherDescription) {
                otherDescriptionsBuffer.push({ id: sec.id, label: sec.label });
            } else {
                flushOtherDescriptions();
                items.push({ title: sec.label, link: `#${sec.id}`, svg: '' });
            }
        });
        flushOtherDescriptions();

        return items;
    }, [contentOrderSections, t]);

    /** Scroll indicator için düz liste (dropdown gruplu değil). */
    const scrollIndicatorSections = useMemo(
        () => contentOrderSections.map((s) => ({ id: s.id, label: s.label })),
        [contentOrderSections]
    );

    return (
        <motion.div 
            className="relative z-10 mx-auto min-h-screen w-full max-w-full min-w-0 px-3 py-8 sm:px-4 sm:py-12 lg:w-9/12 lg:px-4 lg:py-16" 
            initial="hidden" 
            animate="visible" 
            variants={pageVariants}
        >
            <ScrollProgressIndicator sections={scrollIndicatorSections} />

            

            {product?.id != null && (
                <BrochureDownloadModal
                    isOpen={brochureModalOpen}
                    onClose={() => setBrochureModalOpen(false)}
                    productId={product.id}
                    productType="yacht"
                    source="product_detail"
                    prefillEmail={customer?.email ?? null}
                    languageId={languageId ?? undefined}
                />
            )}

            <div className="mt-6 grid grid-cols-12 gap-6 sm:mt-8 sm:gap-8">
                <div className="col-span-12 min-w-0 lg:col-span-8">
                    <div className="sticky top-0 z-[60] mb-4 flex flex-row items-center gap-2 bg-white/95 py-2 backdrop-blur-sm sm:gap-3 lg:top-5 sm:bg-transparent sm:py-0 sm:backdrop-blur-none">
                        <div className="flex-1 min-w-0">
                            <ProductDetailMenu links={displayMenuItems} />
                        </div>
                    </div>
                    <ProductTourHero
                        productName={product?.name || t('tour_detail', 'Tour Detail')}
                        breadcrumbs={breadcrumbs}
                        rating={averageRating}
                        reviewCount={reviewCount}
                        tourDuration={null}
                        groupSize={null}
                        language={language}
                        productId={product?.metas?.yacht_id}
                    />
                    {product?.description && !yachtData.information.content && (
                        <ProductTourDescription
                            description={product?.description || null}
                            shortDescription={null}
                            relevantForYou={null}
                            whatIsExpected={null}
                            language={currentLanguage}
                            translations={translations}
                        />
                    )}
                    <ProductYachtFeatures
                        features={[
                            ...yachtData.features,
                            ...(yachtData.checkIn ? [{ key: 'check_in', label: getVariable('yacht_detail.check_in', 'Check in'), value: yachtData.checkIn }] : []),
                            ...(yachtData.checkOut ? [{ key: 'check_out', label: getVariable('yacht_detail.check_out', 'Check out'), value: yachtData.checkOut }] : []),
                            ...((product?.metas?.rental_type as string)?.trim() ? [{
                                key: 'rental_type',
                                label: getVariable('yacht_detail.rental_type', 'Rental type'),
                                value: String((product?.metas?.rental_type as string) || '').toLowerCase() === 'weekly'
                                    ? getVariable('yacht_detail.rental_type_weekly', 'Weekly')
                                    : String((product?.metas?.rental_type as string) || '').toLowerCase() === 'monthly'
                                        ? getVariable('yacht_detail.rental_type_monthly', 'Monthly')
                                        : getVariable('yacht_detail.rental_type_daily', 'Daily'),
                            }] : []),
                            ...(product?.min_rental_duration != null && product.min_rental_duration > 0 ? [{
                                key: 'min_rental_duration',
                                label: getVariable('yacht_detail.min_rental_duration', 'Minimum rental period'),
                                value: `${product.min_rental_duration} ${getVariable('yacht_request.days', 'days')}`,
                            }] : []),
                        ]}
                        checkIn={null}
                        checkOut={null}
                        title={t('yacht_features', 'Yacht Features')}
                        yachtId={product?.metas?.yacht_id != null ? String(product.metas.yacht_id).replace(/^yacht-/i, '') : null}
                    />

                    <ProductYachtPrices
                        monthlyPrices={yachtData.monthlyPrices}
                        currency={product?.currency?.code || 'EUR'}
                        title={t('price', 'Price')}
                        rentalType={(product?.metas?.rental_type as string) || 'daily'}
                    />
            
                    <ProductYachtEquipment
                        equipment={yachtData.equipment}
                        title={t('equipments', 'Equipments')}
                        expandAllLabel={t('expand_all', 'Expand All')}
                        collapseAllLabel={t('collapse_all', 'Collapse All')}
                    />

                    <ProductYachtConditions
                        conditions={yachtData.conditions}
                        title={t('general_conditions', 'General Conditions')}
                        readConditionsText={t('read_conditions', 'Read Conditions')}
                        conditionsDescription={t('conditions_description', 'Charter conditions include cancellation policies, payment terms, insurance coverage, and responsibilities of both parties. Please read carefully before booking.')}
                    />

                    {((yachtData.includedServices.length > 0 || yachtData.excludedServices.length > 0) ||
                      (yachtData.servicesContent.includedContent || yachtData.servicesContent.excludedContent)) && (
                        <motion.section
                            id="services"
                            className={sectionBlock}
                            variants={slideFromBottomVariants}
                            aria-labelledby="services-heading"
                        >
                            <motion.h2
                                id="services-heading"
                                className="mb-6 text-xl font-bold text-alsero-dark-gray sm:mb-8 lg:text-3xl"
                                variants={headerVariants}
                            >
                                {t('services', 'Services')}
                            </motion.h2>
                            <motion.div
                                className="overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 md:p-8"
                                variants={cardVariants}
                            >
                                {(yachtData.servicesContent.includedContent || yachtData.servicesContent.excludedContent) ? (
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
                                        {yachtData.servicesContent.includedContent && (
                                            <div className="min-w-0">
                                                <h4 className="mb-3 font-semibold text-alsero-dark-gray sm:mb-4">
                                                    {yachtData.servicesContent.includedTitle || t('included_services', 'Included Services')}
                                                </h4>
                                                <div
                                                    className="max-w-full min-w-0 text-alsero-text-tour leading-relaxed prose prose-base max-w-none sm:prose-lg [&_img]:h-auto [&_img]:max-w-full"
                                                    dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(yachtData.servicesContent.includedContent) }}
                                                />
                                            </div>
                                        )}
                                        {yachtData.servicesContent.excludedContent && (
                                            <div className="min-w-0">
                                                <h4 className="mb-3 font-semibold text-alsero-dark-gray sm:mb-4">
                                                    {yachtData.servicesContent.excludedTitle || t('excluded_services', 'Excluded Services')}
                                                </h4>
                                                <div
                                                    className="max-w-full min-w-0 text-alsero-text-tour leading-relaxed prose prose-base max-w-none sm:prose-lg [&_img]:h-auto [&_img]:max-w-full"
                                                    dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(yachtData.servicesContent.excludedContent) }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <ProductTourServicesList
                                        included={yachtData.includedServices}
                                        excluded={yachtData.excludedServices}
                                        language={currentLanguage}
                                        includedTitle={product?.fields?.included_services?.field_title}
                                        excludedTitle={product?.fields?.excluded_services?.field_title}
                                    />
                                )}
                            </motion.div>
                        </motion.section>
                    )}

                    {yachtData.tabFields.map((tabGroup) => (
                            <motion.section
                                key={tabGroup.sectionId}
                                id={tabGroup.sectionId}
                                className={sectionBlock}
                                variants={slideFromBottomVariants}
                                aria-labelledby={`${tabGroup.sectionId}-heading`}
                            >
                                <motion.h2
                                    id={`${tabGroup.sectionId}-heading`}
                                    className="mb-6 text-xl font-bold text-alsero-dark-gray sm:mb-8 lg:text-3xl"
                                    variants={headerVariants}
                                >
                                    {resolveRepeaterSectionTitle(tabGroup.tabName, tabGroup.tabName, t)}
                                </motion.h2>
                                {tabGroup.fields.map((field) => (
                                    <motion.div
                                        key={field.fieldKey}
                                        className="mb-6 last:mb-0 sm:mb-8"
                                        variants={cardVariants}
                                    >
                                        {tabGroup.fields.length > 1 && (
                                            <h3 className="mb-3 text-lg font-semibold text-alsero-dark-gray sm:mb-4 sm:text-xl">
                                                {resolveRepeaterSectionTitle(field.fieldTitle, field.fieldTitle, t)}
                                            </h3>
                                        )}
                                        <div
                                            className="max-w-full min-w-0 text-alsero-text-tour leading-relaxed prose prose-base max-w-none sm:prose-lg [&_img]:h-auto [&_img]:max-w-full"
                                            dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(field.content) }}
                                        />
                                    </motion.div>
                                ))}
                            </motion.section>
                    ))}

                    {productFaqs && productFaqs.length > 0 && (
                        <motion.section 
                            id="faqs"
                            className="mb-10 scroll-mt-20 sm:mb-12 sm:scroll-mt-24 lg:mb-16"
                            variants={slideFromBottomVariants}
                            aria-labelledby="faqs-heading"
                        >
                            <Faqs language={currentLanguage} generalFaqs={productFaqs} />
                        </motion.section>
                    )}
                </div>

                <div className="col-span-12 min-w-0 lg:col-span-4">
                    <div className="space-y-4 lg:sticky lg:top-24">
                        <ReservationBookingSidebar
                            productId={product?.id || 0}
                            productType={product?.type || product?.product_type || 'yacht'}
                            productName={product?.name}
                            hasPrice={product?.has_price ?? (Array.isArray(product?.monthly_prices) && (product.monthly_prices?.length ?? 0) > 0) ?? false}
                            bookingFlow={(product?.booking_flow as 'request_first' | 'instant_online' | 'bank_transfer_only') ?? 'request_first'}
                            language={currentLanguage}
                            languageId={languageId || undefined}
                            agreements={reservationFormAgreements}
                            translations={translations}
                            initialRequestFormDetails={initialRequestFormDetails}
                            minRentalDuration={product?.min_rental_duration ?? null}
                            periodStartDate={product?.period_start_date ?? null}
                            periodEndDate={product?.period_end_date ?? null}
                            rentalType={(product?.metas?.rental_type as string) || undefined}
                            onReservationSubmit={() => {}}
                        />
                        {/* Tur detaydaki gibi: sidebar altta favori, paylaş, broşür (metinli butonlar) */}
                        <motion.div className="max-w-full overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5" variants={cardVariants}>
                            <div className="space-y-2">
                                <motion.button
                                    type="button"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={toggleFavorite}
                                    disabled={isFavoriteLoading}
                                    className={`w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-left ${
                                        isFavorite ? 'bg-alsero-first text-white' : 'bg-gray-100 text-alsero-dark-gray hover:bg-gray-200'
                                    } ${isFavoriteLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    aria-label={isFavorite ? t('yacht_detail.remove_from_favorites', 'Favorilerden Çıkar') : t('yacht_detail.add_to_favorites', 'Favorilere Ekle')}
                                >
                                    <svg className="w-4 h-4 shrink-0" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                    <span>{isFavorite ? t('yacht_detail.remove_from_favorites', 'Favorilerden Çıkar') : t('yacht_detail.add_to_favorites', 'Favorilere Ekle')}</span>
                                </motion.button>
                                <div className="w-full">
                                    <ProductShareButton
                                        url={typeof window !== 'undefined' ? window.location.href : ''}
                                        title={product?.name || 'Yacht - Alsero Tours'}
                                        translationPrefix="yacht_detail"
                                        showLabel
                                    />
                                </div>
                                {product?.id != null && (
                                    <motion.button
                                        type="button"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => setBrochureModalOpen(true)}
                                        className="w-full flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2.5 text-sm font-medium text-alsero-dark-gray hover:bg-gray-200 text-left"
                                        aria-label={t('tour_card_download_brochure', 'İndir Broşür')}
                                    >
                                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>{t('brochure_download_button_tooltip', 'Broşür indir')}</span>
                                    </motion.button>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>
                
                <div className="col-span-12 min-w-0">
                    <motion.section 
                        ref={reviewsTriggerRef}
                        id="reviews"
                        className={sectionBlock}
                        variants={slideFromBottomVariants}
                        aria-labelledby="reviews-heading"
                    >
                        <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-12">
                            <div className="min-w-0 lg:col-span-8">
                                <ProductReviewsList
                                    reviews={reviews}
                                    averageRating={averageRating}
                                    reviewCount={reviewCount}
                                    isLoading={reviewsLoading}
                                    language={currentLanguage}
                                    productId={product?.id || null}
                                    languageId={languageId || null}
                                    onLoadMore={async () => {
                                        if (!product?.id || !languageId) return;
                                        const nextPage = currentPage + 1;
                                        try {
                                            const params = new URLSearchParams({
                                                reviewable_type: 'product',
                                                reviewable_id: String(product.id),
                                                status: 'approved',
                                                language_id: String(languageId),
                                                per_page: '15',
                                                page: String(nextPage),
                                            });
                                            const response = await fetch(`/api/customer-reviews?${params.toString()}`);
                                            if (response.ok) {
                                                const data = await response.json();
                                                setHasMoreReviews(data.meta?.has_more || false);
                                                await refetchReviews();
                                                setCurrentPage(nextPage);
                                            }
                                        } catch (error) {
                                            if (process.env.NODE_ENV === 'development') {
                                                console.error('Load more reviews error:', error);
                                            }
                                        }
                                    }}
                                    hasMore={hasMoreReviews}
                                    translations={translations}
                                />
                            </div>
                            <div className="min-w-0 lg:col-span-4">
                                <div className="lg:sticky lg:top-24">
                                    {effectiveReviewForm && (
                                        <ProductReviewForm
                                            productId={product?.id || null}
                                            productType="yacht"
                                            language={currentLanguage}
                                            languageId={languageId || null}
                                            form={effectiveReviewForm}
                                            translations={translations}
                                            pageUrls={pageUrls}
                                            contractsPageUrl={contractsPageUrl}
                                            defaultLanguage={defaultLanguage}
                                            initialFormDetails={initialFormDetails}
                                            onFormSubmit={async () => {
                                                try {
                                                    await refetchReviews();
                                                } catch {
                                                    /* Timeout/AbortError - unhandled rejection önlenir */
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                    </div>
                    </motion.section>
                </div>
            </div>
        </motion.div>
    );
}
